import { useState, useEffect } from 'react';
import { FormControl, FormLabel, Text, Stack, Box } from '@chakra-ui/react';
import MultiSelect from '../../../../../components/presentational/ChipInsertPalette';
import TriggerFieldToolTip from './TriggerFieldToolTip';
import { useTriggerForm } from '../useTriggerForm.js';
import walkthroughIds from '../../walkthroughIds';
import {
    formLabelStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';

// Guards the document-wide focus/click listeners below from hijacking unrelated inputs (e.g. Trigger Name).
const INSERTABLE_FIELD_ID_PATTERN =
    /^(subject|body|uri|emailAddressField|webHookHeader(Key|Value)-\d+)$/;

// The chip palette renders its own FormControl, so this label needs the id of
// the palette's input to point at — react-select takes it as `inputId` (2930).
const FIELDS_SELECT_INPUT_ID = 'triggerFieldsSelect';

const FieldsSelector = ({
    darkMode,
    fields,
    cursorPosition,
    setCursorPosition,
}) => {
    const { editMode } = walkthroughIds;
    const [isHovered, setIsHovered] = useState(false);
    const [activeTextareaField, setActiveTextareaField] = useState(null);
    const { updateFormData, headers, setHeaders, setEmailAddressTmp } =
        useTriggerForm();

    const setActionState = (payload) => {
        const fieldPath = `action.${activeTextareaField}`;
        updateFormData(fieldPath, payload[activeTextareaField]);
    };

    const extractHeaderIdFromActiveField = (activeTextareaField) => {
        if (
            !activeTextareaField ||
            !activeTextareaField.startsWith('webHookHeader')
        ) {
            return null;
        }

        const match = activeTextareaField.match(
            /webHookHeader(?:Key|Value)-(\d+)/,
        );
        return match ? parseInt(match[1], 10) : null;
    };

    const getHeaderFieldType = (activeTextareaField) => {
        if (
            !activeTextareaField ||
            !activeTextareaField.startsWith('webHookHeader')
        ) {
            return null;
        }

        if (activeTextareaField.includes('Key-')) {
            return 'key';
        } else if (activeTextareaField.includes('Value-')) {
            return 'value';
        }
        return null;
    };

    const setMultiSelectState = (payload) => {
        if (activeTextareaField?.startsWith('emailAddressField')) {
            // Inserted token goes into the not-yet-committed text buffer, not the committed emailAddress array.
            const emailAddressBuffer = Object.values(payload)[0];
            if (emailAddressBuffer) {
                setEmailAddressTmp(emailAddressBuffer);
            }
            return;
        }

        if (activeTextareaField?.startsWith('webHookHeader')) {
            const headerId =
                extractHeaderIdFromActiveField(activeTextareaField);
            const fieldType = getHeaderFieldType(activeTextareaField);

            if (headerId !== null && fieldType) {
                const fieldValue = Object.values(payload)[0] || '';
                const currentHeaders = Array.isArray(headers) ? headers : [];
                const existingHeaderIndex = currentHeaders.findIndex(
                    (header) => header.id === headerId,
                );

                let updatedHeaders;
                if (existingHeaderIndex >= 0) {
                    updatedHeaders = [...currentHeaders];
                    updatedHeaders[existingHeaderIndex] = {
                        ...updatedHeaders[existingHeaderIndex],
                        [fieldType]: fieldValue,
                    };
                } else {
                    const newHeader = {
                        id: headerId,
                        key: fieldType === 'key' ? fieldValue : '',
                        value: fieldType === 'value' ? fieldValue : '',
                    };
                    updatedHeaders = [...currentHeaders, newHeader];
                }

                setHeaders(updatedHeaders);
            }
            return;
        }
        setActionState(payload);
    };

    useEffect(() => {
        const handleFocus = (e) => {
            const target = e.target;
            if (
                (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') &&
                INSERTABLE_FIELD_ID_PATTERN.test(target.id)
            ) {
                setActiveTextareaField(target.id);
            }
        };

        const handleClick = (e) => {
            const target = e.target;
            if (
                (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') &&
                INSERTABLE_FIELD_ID_PATTERN.test(target.id)
            ) {
                setActiveTextareaField(target.id);
            }
        };

        document.addEventListener('focusin', handleFocus);
        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('focusin', handleFocus);
            document.removeEventListener('click', handleClick);
        };
    }, []);

    return (
        <FormControl
            sx={{ margin: '0 !important', position: 'relative' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Box marginBottom={4}>
                <Box>
                    <FormLabel
                        htmlFor={FIELDS_SELECT_INPUT_ID}
                        sx={formLabelStyling(darkMode)}>
                        Fields
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Click on fields to insert them into your content.
                    </Text>
                </Box>
                <Box paddingTop="15px">
                    <Stack direction="row" width="100%" minWidth="800px">
                        <Box width={733} minWidth={733}>
                            <MultiSelect
                                inputId={FIELDS_SELECT_INPUT_ID}
                                setCursorPosition={setCursorPosition}
                                cursorPosition={cursorPosition}
                                label=""
                                staticEnum={fields}
                                activeTextareaID={activeTextareaField}
                                activeTextareaField={activeTextareaField}
                                setState={setMultiSelectState}
                                placeholder=""
                                data-walkthroughid={editMode.ufFieldsTags}
                            />
                        </Box>
                        <TriggerFieldToolTip
                            isVisible={isHovered}
                            label="Click on any field to insert it into the content at the current cursor position. These fields will be dynamically replaced with actual values when triggered."
                            ariaLabel="fields-selector-tip"
                        />
                    </Stack>
                </Box>
            </Box>
        </FormControl>
    );
};

export default FieldsSelector;
